import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { selectEnhancedConditionStatus } from "../../../features/enhanced-condition-status/enhancedConditionStatusSlice";

// mui components
import { Box, Stack, TableCell, TableRow, Tooltip } from "@mui/material";

// custom components
import Body3 from "../../typography/body-03";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
// custom styles
import styles from "../index.module.scss";

const normalizeText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		const text = normalizeText(value);
		if (text) return text;
	}

	return "";
};

const mapRemainingDocumentsToAssignedTo = (documents) =>
	(documents || []).map((document) => ({
		entityId: firstNonEmpty(
			document?.id,
			document?.entityId,
			document?.documentId
		),
		entityName: firstNonEmpty(
			document?.fileName,
			document?.entityName,
			document?.name,
			document?.title,
			"Untitled Document"
		),
		entityType: "Document",
	}));

const EnhancedConditionTrackingRow = ({
	row,
	toggleDrawer,
	loanId,
	setDocumentsList = () => {},
	extraData = {},
	activeRowId = null,
	setActiveRowId = () => {},
}) => {
	const conditionId = firstNonEmpty(row?.id, row?.entityId, row?.conditionId);
	const rowDocuments = Array.isArray(row?.documents) ? row.documents : [];
	const rowCountOfDoc = Number(row?.count_of_doc);
	const supportingDocumentsCount =
		Number.isFinite(rowCountOfDoc) && rowCountOfDoc > 0
			? rowCountOfDoc
			: Array.isArray(row?.supportingDocuments)
				? row.supportingDocuments.length
				: 0;
	const loanAttachmentDocuments = Array.isArray(
		extraData?.loanAttachmentDocuments
	)
		? extraData.loanAttachmentDocuments
		: [];

	const [isNotesTooltipOpen, setIsNotesTooltipOpen] = useState(false);
	const enhancedConditionsStatus = useSelector(selectEnhancedConditionStatus);
	const isEnhancedConditionsEnabled =
		String(enhancedConditionsStatus ?? "")
			.trim()
			.toUpperCase() === "Y";
	const descriptionText = normalizeText(
		isEnhancedConditionsEnabled ? row.externalDescription : row.description
	);
	const rowSelectionId = firstNonEmpty(
		row?.entityId,
		row?.id,
		row?.conditionId
	);
	const isActiveRow = Boolean(activeRowId)
		? String(activeRowId) === String(rowSelectionId)
		: false;

	const handleTitleClick = (event) => {
		event?.stopPropagation?.();
		if (rowSelectionId) {
			setActiveRowId?.(rowSelectionId);
		}

		toggleDrawer?.(event, "enhanced-condition-detail", {
			selectedDocument: row,
			row,
			conditionTitle: row?.title ?? row?.conditionTitle ?? "",
			count_of_doc: supportingDocumentsCount,
			supportingDocuments: Array.isArray(row?.supportingDocuments)
				? row.supportingDocuments
				: [],
			selectedConditionType: row?.conditionType ?? "",
			matchingConditionDocuments: [row],
			loanId: loanId ?? row?.loanGuid ?? row?.loanId,
			loanAttachmentDocuments: extraData?.loanAttachmentDocuments || [],
			onLoanAttachmentDocumentsRefresh:
				extraData?.updateLoanAttachmentDocuments,
			onConditionListRefresh: extraData?.onConditionListRefresh,
			onSupportingDocumentsRefresh: (refreshedSupportingDocuments = []) => {
				const nextSupportingDocuments = Array.isArray(
					refreshedSupportingDocuments
				)
					? refreshedSupportingDocuments
					: [];

				extraData?.onSupportingDocumentsRefresh?.(
					conditionId,
					nextSupportingDocuments
				);
			},
			onUnlinkSuccess: ({
				conditionId: unlinkedConditionId,
				remainingDocuments = [],
			}) => {
				const targetConditionId = String(
					unlinkedConditionId ?? conditionId ?? row?.id ?? row?.entityId ?? ""
				);

				const nextAssignedTo =
					mapRemainingDocumentsToAssignedTo(remainingDocuments);

				extraData?.onSupportingDocumentsRefresh?.(
					targetConditionId,
					remainingDocuments
				);

				setDocumentsList((previousDocuments) =>
					(previousDocuments || []).map((documentRow) => {
						const documentRowId = String(
							documentRow?.id ??
								documentRow?.entityId ??
								documentRow?.conditionId ??
								""
						);

						if (documentRowId !== targetConditionId) {
							return documentRow;
						}

						return {
							...documentRow,
							assignedTo: nextAssignedTo,
							supportingDocuments: remainingDocuments,
							documents: remainingDocuments,
							linkedDocuments: remainingDocuments,
						};
					})
				);
			},
		});
	};

	const handleRowClick = (event) => {
		handleTitleClick(event);
	};

	return (
		<TableRow
			hover
			tabIndex={0}
			key={row.label}
			role="button"
			onClick={handleRowClick}
			sx={{
				cursor: "pointer",
				"& > .MuiTableCell-root": {
					backgroundColor: isActiveRow ? "#EAF8EF" : "transparent",
				},
				"&:hover > .MuiTableCell-root": {
					backgroundColor: isActiveRow ? "#DDF4E4" : "#F3FAF5",
				},
				"&:focus-visible > .MuiTableCell-root": {
					backgroundColor: isActiveRow ? "#DDF4E4" : "#F3FAF5",
				},
			}}
		>
			<TableCell>
				<Stack direction="column" gap={0.4}>
					<Body3
						text={row.title}
						extraClass={`${styles.greenText} ${styles.clickableText}`}
						handleClick={handleTitleClick}
					/>
					{descriptionText ? (
						<Tooltip
							arrow
							placement="top-start"
							disableInteractive
							enterDelay={150}
							leaveDelay={80}
							open={isNotesTooltipOpen}
							onClose={() => setIsNotesTooltipOpen(false)}
							title={
								<Box className={styles.notesTooltipCard}>
									<Box className={styles.notesTooltipIconWrap}>
										<ChatBubbleOutlineOutlinedIcon
											className={styles.notesTooltipIcon}
										/>
									</Box>

									<Stack className={styles.notesTooltipContent}>
										<Box className={styles.notesTooltipHeader}>
											Condition Description
										</Box>
										<Box className={styles.notesTooltipBody}>
											{descriptionText}
										</Box>
									</Stack>
								</Box>
							}
							componentsProps={{
								tooltip: {
									className: styles.notesTooltip,
									sx: {
										maxWidth: "none",
										padding: 0,
										backgroundColor: "transparent",
										boxShadow: "none",
									},
								},
								arrow: {
									className: styles.notesTooltipArrow,
									sx: {
										color: "#ffffff",
										"&::before": {
											border: "1px solid #d9dee7",
										},
									},
								},
							}}
						>
							<Box
								component="button"
								type="button"
								aria-label={`Condition Description: ${descriptionText}`}
								className={styles.notesPreviewButton}
								onMouseEnter={() => setIsNotesTooltipOpen(true)}
								onMouseLeave={() => setIsNotesTooltipOpen(false)}
								onFocus={() => setIsNotesTooltipOpen(true)}
								onBlur={() => setIsNotesTooltipOpen(false)}
								onClick={(event) => event.stopPropagation()}
								onMouseDown={(event) => event.stopPropagation()}
							>
								<ChatBubbleOutlineOutlinedIcon
									className={styles.notesPreviewIcon}
								/>
								<Box component="span" className={styles.notesPreviewText}>
									{descriptionText}
								</Box>
							</Box>
						</Tooltip>
					) : null}
				</Stack>
			</TableCell>
			<TableCell>
				<Body3 text={row.sub_status} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Body3 text={row.category} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Body3 text={row.priorTo} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Body3
					text={row.source ? row.source : " -"}
					extraClass={styles.greenText}
				/>
			</TableCell>
			<TableCell>
				<Body3
					text={
						row?.createdDate
							? `${DateTime.fromISO(row.createdDate, {
									setZone: true,
								})
									.setZone("America/New_York")
									.toFormat("MMM d, yyyy, h:mm a")} ET`
							: ""
					}
					extraClass={styles.greenText}
				/>
			</TableCell>
			<TableCell>
				<Box
					className={styles.assignedCountBadge}
					title={`${supportingDocumentsCount} documents`}
				>
					{supportingDocumentsCount}
				</Box>
			</TableCell>
		</TableRow>
	);
};

export default EnhancedConditionTrackingRow;
